import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, Upload, CheckCircle2, ArrowRight, FileText, AlertCircle } from 'lucide-react';
import AEDIcon from '../components/icons/AEDIcon';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuth } from '@/context/AuthContext';
import api from '@/services/api';

export const OnboardingPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [kycData, setKycData] = useState({
    document_type: 'emirates_id',
    document_number: '',
    expiry_date: ''
  });
  const [pofData, setPofData] = useState({
    bank_name: '',
    account_type: 'savings',
    available_balance: '',
    currency: 'AED'
  });
  const [completed, setCompleted] = useState({ kyc: false, pof: false });

  const handleKYCSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/kyc/submit', kycData);
      setCompleted({ ...completed, kyc: true });
      if (user?.role === 'buyer') {
        setStep(2);
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('KYC submission failed:', error);
    }
    setLoading(false);
  };

  const handlePOFSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/kyc/proof-of-funds', {
        ...pofData,
        available_balance: parseFloat(pofData.available_balance)
      });
      setCompleted({ ...completed, pof: true });
      navigate('/dashboard');
    } catch (error) {
      console.error('POF submission failed:', error);
    }
    setLoading(false);
  };

  const totalSteps = user?.role === 'buyer' ? 2 : 1;

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <div className="noise-overlay" />
      <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary/10 rounded-full blur-3xl" />
      
      <div className="w-full max-w-lg relative z-10">
        <Card className="glass-card border-white/10">
          <CardHeader className="text-center pb-2">
            <div className="w-16 h-16 rounded bg-primary flex items-center justify-center mx-auto mb-4">
              {step === 1 ? <FileText className="w-8 h-8 text-primary-foreground" /> : <AEDIcon className="w-8 h-8 text-primary-foreground" />}
            </div>
            <CardTitle className="font-display text-2xl">
              {step === 1 ? 'KYC Verification' : 'Proof of Funds'}
            </CardTitle>
            <CardDescription>
              {step === 1 
                ? 'Verify your identity to access the platform' 
                : 'Verify your financial capacity to view properties'
              }
            </CardDescription>
            
            {/* Progress */}
            <div className="flex gap-2 justify-center mt-4">
              {Array(totalSteps).fill(0).map((_, idx) => (
                <div key={idx} className={`w-8 h-1 rounded ${step > idx ? 'bg-primary' : 'bg-secondary'}`} />
              ))}
            </div>
          </CardHeader>
          
          <CardContent className="pt-6">
            {/* Important Notice */}
            <div className="flex gap-3 p-4 rounded bg-primary/10 border border-primary/30 mb-6">
              <Shield className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <strong className="text-primary">Your data is protected.</strong>
                <p className="text-muted-foreground mt-1">
                  We verify your documents but never store them. Your privacy is guaranteed.
                </p>
              </div>
            </div>
            
            {step === 1 ? (
              <form onSubmit={handleKYCSubmit} className="space-y-6">
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-widest text-muted-foreground">
                    Document Type
                  </Label>
                  <Select
                    value={kycData.document_type}
                    onValueChange={(value) => setKycData({ ...kycData, document_type: value })}
                  >
                    <SelectTrigger className="bg-transparent border-white/20 focus:border-primary h-12" data-testid="kyc-doc-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="emirates_id">Emirates ID</SelectItem>
                      <SelectItem value="passport">Passport</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-widest text-muted-foreground">
                    Document Number
                  </Label>
                  <Input
                    placeholder="784-XXXX-XXXXXXX-X"
                    value={kycData.document_number}
                    onChange={(e) => setKycData({ ...kycData, document_number: e.target.value })}
                    className="bg-transparent border-white/20 focus:border-primary h-12"
                    required
                    data-testid="kyc-doc-number-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-widest text-muted-foreground">
                    Expiry Date
                  </Label>
                  <Input
                    type="date"
                    value={kycData.expiry_date}
                    onChange={(e) => setKycData({ ...kycData, expiry_date: e.target.value })}
                    className="bg-transparent border-white/20 focus:border-primary h-12"
                    data-testid="kyc-expiry-input"
                  />
                </div>
                
                <Button
                  type="submit"
                  className="w-full bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-widest text-xs font-bold h-12"
                  disabled={loading}
                  data-testid="kyc-submit-btn"
                >
                  {loading ? 'Verifying...' : 'Verify Identity'}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </form>
            ) : (
              <form onSubmit={handlePOFSubmit} className="space-y-6">
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-widest text-muted-foreground">
                    Bank Name
                  </Label>
                  <Input
                    placeholder="Emirates NBD"
                    value={pofData.bank_name}
                    onChange={(e) => setPofData({ ...pofData, bank_name: e.target.value })}
                    className="bg-transparent border-white/20 focus:border-primary h-12"
                    required
                    data-testid="pof-bank-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-widest text-muted-foreground">
                    Account Type
                  </Label>
                  <Select
                    value={pofData.account_type}
                    onValueChange={(value) => setPofData({ ...pofData, account_type: value })}
                  >
                    <SelectTrigger className="bg-transparent border-white/20 focus:border-primary h-12" data-testid="pof-account-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="savings">Savings Account</SelectItem>
                      <SelectItem value="current">Current Account</SelectItem>
                      <SelectItem value="investment">Investment Account</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-widest text-muted-foreground">
                    Available Balance (AED)
                  </Label>
                  <Input
                    type="number"
                    placeholder="5,000,000"
                    value={pofData.available_balance}
                    onChange={(e) => setPofData({ ...pofData, available_balance: e.target.value })}
                    className="bg-transparent border-white/20 focus:border-primary h-12"
                    required
                    data-testid="pof-balance-input"
                  />
                </div>
                
                <Button
                  type="submit"
                  className="w-full bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-widest text-xs font-bold h-12"
                  disabled={loading}
                  data-testid="pof-submit-btn"
                >
                  {loading ? 'Verifying...' : 'Verify Funds'}
                  <CheckCircle2 className="w-4 h-4 ml-2" />
                </Button>
              </form>
            )}
            
            <Button
              variant="ghost"
              className="w-full mt-4 text-muted-foreground hover:text-primary"
              onClick={() => navigate('/dashboard')}
            >
              Skip for now
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default OnboardingPage;
